import { FlagIcon } from "@xola/icons";
import { Badge, Checkbox } from "@xola/ui-kit";
import React, { useEffect, useState } from "react";
import { SelectionState } from "types";
import { FilterMenuButton } from "@/components/Dashboard/Filters/FilterMenuButton";
import { FilterItem } from "@/components/Dashboard/Filters/ListFilter";
import { MultiSelectPopover } from "@/components/MultiSelectPopover";
import { useSelection } from "@/hooks/useSelection";
import { useVisibility } from "@/hooks/useVisibility";

export interface TravelerMessage {
    id: ID;
    type: "traveler";
    name: string;
}

interface ScheduleSelectorProps {
    value: SelectionState;
    onChange: (value: SelectionState) => void;
    travelersList: TravelerMessage[];
    popoverParentId?: string;
}

const defaultValue: SelectionState = {
    all: true,
    selected: [],
};

export const TravelersSelector = ({
    value = defaultValue,
    onChange,
    travelersList,
    popoverParentId,
}: ScheduleSelectorProps) => {
    const [visible, { toggle, hide }] = useVisibility(false);
    const [initialSelection, setInitialSelection] = useState(value);

    const selection = useSelection(travelersList, false, value);

    const [selectedTravelersWithName, setSelectedTravelersWithName] = useState(() => {
        if (selection.isSelectedAll) {
            return [...travelersList];
        }

        return [...travelersList].filter((product) => selection.state.selected.includes(product.id));
    });
    const travelersListInputText = selectedTravelersWithName.map((traveler) => traveler.name).join(", ");

    const handleCheckboxChange = (traveler: TravelerMessage) => {
        if (selection.isSelectedAll) {
            setSelectedTravelersWithName([...travelersList].filter((item) => item.id !== traveler.id));
        } else if (selectedTravelersWithName.some((item) => item.id === traveler.id)) {
            setSelectedTravelersWithName([...selectedTravelersWithName.filter((item) => item.id !== traveler.id)]);
        } else {
            setSelectedTravelersWithName([...selectedTravelersWithName, traveler]);
        }

        selection.select(traveler);
    };

    const handleApply = () => {
        onChange(selection.state);
        hide();
    };

    const handleCancel = () => {
        setSelectedTravelersWithName([...travelersList].filter((item) => initialSelection.selected.includes(item.id)));

        selection.setState(initialSelection);
        hide();
    };

    const handleSelectAll = () => {
        if (selection.isSelectedAll) {
            setSelectedTravelersWithName([]);
        }

        selection.selectAll();
    };

    useEffect(() => {
        if (visible) {
            setInitialSelection(value);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    return (
        <div>
            <MultiSelectPopover
                isVisible={visible}
                showSearch={false}
                placement="bottom-end"
                className="min-w-[420px]"
                popoverParentId={popoverParentId}
                selectedCount={selection.state.all ? "All" : selection.state.selected.length}
                target={
                    <FilterMenuButton
                        isDropdown
                        showTooltipWithFullText
                        className="!w-full !max-w-full"
                        text={selection.isSelectedAll ? "All" : travelersListInputText}
                        onClick={toggle}
                    />
                }
                onCancel={handleCancel}
                onApply={handleApply}
            >
                <MultiSelectPopover.Item key="selectAll" className="bg-gray-lighter">
                    <Checkbox
                        className="font-bold"
                        checked={selection.isSelectedAll}
                        label="Select All"
                        onChange={handleSelectAll}
                    />
                    <Badge className="text-sm text-gray-darker" size="small">
                        {selection.count} Selected
                    </Badge>
                </MultiSelectPopover.Item>

                {travelersList.map((traveler, index) => (
                    <FilterItem
                        key={traveler.id}
                        label={traveler.name}
                        isChecked={selection.isSelected(traveler)}
                        className="h-12 border-b border-gray-light px-3 py-4 font-bold last:border-b-0"
                        labelIcon={index === 0 && <FlagIcon />}
                        onChange={() => handleCheckboxChange(traveler)}
                    />
                ))}
            </MultiSelectPopover>
        </div>
    );
};
